import { useCallback, useMemo } from "react";
import { useRouter } from "next/router";

export function useSearchParameters<T extends Record<string, string | null>>() {
  const router = useRouter();
  const { companySlug } = router.query;

  const parameters = useMemo(() => {
    const params = new URLSearchParams(router.asPath.split("?")[1]);
    return Array.from(params.entries()).reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {} as Record<string, string>);
  }, [router.asPath]);

  const setParameters = useCallback(
    (updates: Partial<T> | ((prev: T) => Partial<T>)) => {
      const newParams = new URLSearchParams(router.asPath.split("?")[1]);

      const updatedParameters =
        typeof updates === "function" ? updates(parameters as T) : updates;

      Object.entries(updatedParameters).forEach(([key, value]) => {
        if (value === null) {
          newParams.delete(key);
        } else {
          newParams.set(key, value);
        }
      });

      const pathname = router.pathname.replace(
        "[companySlug]",
        companySlug as string
      );
      const search = newParams.toString();
      const query = search ? `?${search}` : "";
      return router.push(`${pathname}${query}`, undefined, {
        shallow: true,
      });
    },
    [router, parameters, companySlug]
  );

  return [parameters, setParameters] as const;
}
